import {Component, inject, computed, signal, ChangeDetectionStrategy} from "@angular/core"
import {CM_LOGGER_API} from "@app/common/services/logging"
import {FormsModule} from "@angular/forms"
import {MatDialogRef} from "@angular/material/dialog"
import {MatFormFieldModule} from "@angular/material/form-field"
import {MatInputModule} from "@angular/material/input"
import {MatSelectModule} from "@angular/material/select"
import {MutationCreateTagInput, TagType} from "@generated"
import {AddItemDialogComponent} from "@common/components/dialogs/add-item-dialog/add-item-dialog.component"
import {ColorInputComponent} from "@common/components/inputs/color-input/color-input.component"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {StringInputComponent} from "@common/components/inputs/string-input/string-input.component"
import {NativeInputTextAreaComponent} from "@common/components/inputs/native/native-input-text-area/native-input-text-area.component"
import {OrganizationSelectComponent} from "@common/components/inputs/select/organization-select/organization-select.component"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {AuthService} from "@common/services/auth/auth.service"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {Enums} from "@enums"
import {Labels} from "@labels"
import {AddTagGQL} from "@platform/components/tags/add-tag-dialog/add-tag-dialog.generated"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-add-tag-dialog",
    imports: [
        FormsModule,
        AddItemDialogComponent,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        InputContainerComponent,
        StringInputComponent,
        NativeInputTextAreaComponent,
        ColorInputComponent,
        OrganizationSelectComponent,
    ],
    templateUrl: "./add-tag-dialog.component.html",
    styleUrl: "./add-tag-dialog.component.scss",
})
export class AddTagDialogComponent {
    private readonly logger = inject(CM_LOGGER_API)
    protected readonly Enums = Enums
    protected readonly Labels = Labels

    private dialogRef = inject(MatDialogRef<AddTagDialogComponent>)
    readonly auth = inject(AuthService)
    readonly organizations = inject(OrganizationsService)
    readonly permission = inject(PermissionsService)
    readonly addTagGql = inject(AddTagGQL)
    $can = this.permission.$to
    readonly $isLoading = signal(false)

    readonly $data = signal<Omit<MutationCreateTagInput, "organizationId"> & {color: string}>({
        name: "",
        type: TagType.MaterialRange,
        description: "",
        color: "",
    })

    readonly $organizationId = computed<string | undefined>(() => this.organizations.$current()?.id)
    selectedOrganizationId = this.$organizationId()

    updateOrganization = async (data: {organizationId: string}) => {
        this.selectedOrganizationId = data.organizationId
    }

    readonly $isFormValid = computed(() => !!this.$data().name)

    async onConfirm() {
        if (!this.$isFormValid()) {
            return
        }

        this.$isLoading.set(true)
        try {
            const result = await mutateThrowingErrors(this.addTagGql)({
                input: {...this.$data(), organizationId: this.selectedOrganizationId ?? this.$organizationId()},
            })
            this.dialogRef.close(result.createTag)
        } catch (error) {
            this.logger.error("Failed to create tag:", error)
            alert(`Failed to create tag: ${error}`)
        } finally {
            this.$isLoading.set(false)
        }
    }

    readonly $tagTypes = computed(() => Array.from(Labels.TagType.values()).filter((label) => this.$can().read.tagType(label.state)))

    updateDataType(type: TagType): void {
        this.$data.update((r) => ({...r, type}))
    }

    updateDataColor(color: string): void {
        this.$data.update((r) => ({...r, color}))
    }
}
